/*!
 * Copyright (c) REWE Digital GmbH
 */
import { Host, h } from "@stencil/core";
/**
 * VxAddToCartCount component.
 */
export class VxAddToCartCount {
    constructor() {
        /**
         * Debounce Timeout.
         */
        this.debounceTimeout = null;
        /**
         * Is disabled.
         */
        this.isDisabled = false;
        /**
         * Inner quantity.
         */
        this.innerQuantity = 0;
        /**
         * Handle quantity change.
         */
        this.quantityChange = (increased) => {
            if (increased && this.isDisabled) {
                return;
            }
            if (this.debounceTimeout) {
                clearTimeout(this.debounceTimeout);
                this.debounceTimeout = null;
            }
            this.innerQuantity = this.quantity;
            this.debounceTimeout = setTimeout(() => {
                this.handleQuantityChange.emit({
                    quantity: this.quantity,
                    productId: this.productId,
                    context: this.context,
                    increased: increased,
                });
            }, this.debounceTime);
        };
        /**
         * Add Item from cart.
         */
        this.addItem = () => {
            if (!this.isDisabled && !this.hitLimit) {
                this.quantity = this.quantity < this.maxQuantity ? this.quantity + 1 : this.quantity = this.maxQuantity;
                this.numberInput.value = this.quantity.toString();
                this.quantityChange(true);
            }
        };
        /**
         * Remove Item from cart.
         */
        this.removeItem = () => {
            if (this.quantity > 0) {
                this.quantity = this.quantity > 0 ? this.quantity - 1 : this.quantity = 0;
                this.numberInput.value = this.quantity.toString();
                this.quantityChange(false);
            }
        };
        /**
         * Handle quantity input change.
         */
        this.handleInputChange = (e) => {
            const target = e.target;
            const oldQuantity = this.quantity;
            if (this.maxQuantity >= parseInt(target.value, 10)) {
                this.quantity = parseInt(target.value, 10);
            }
            else {
                this.quantity = this.maxQuantity;
                this.numberInput.value = this.quantity.toString();
            }
            this.quantityChange(this.quantity > oldQuantity);
        };
        /**
         * Handle key press.
         */
        this.handleKeyPress = (e) => {
            if (['ArrowUp', 'ArrowDown', 'Escape', 'Home', 'End'].includes(e.key)) {
                const oldQuantity = this.quantity;
                switch (e.key) {
                    case 'ArrowUp':
                        if (!this.isDisabled) {
                            this.addItem();
                        }
                        break;
                    case 'ArrowDown':
                        this.removeItem();
                        break;
                    case 'Escape':
                        this.numberInput.value = '0';
                        break;
                    case 'Home':
                        if (!this.isDisabled) {
                            this.numberInput.value = '1';
                        }
                        break;
                    case 'End':
                        if (!this.isDisabled) {
                            this.numberInput.value = this.maxQuantity.toString();
                        }
                        break;
                }
                if (!this.isDisabled) {
                    this.quantity = parseInt(this.numberInput.value, 10);
                    this.quantityChange(this.quantity > oldQuantity);
                }
                e.preventDefault();
                e.stopPropagation();
            }
        };
        /**
         * Handle focus event.
         */
        this.handleFocus = () => {
            if (!this.isDisabled) {
                this.addToCartFocus.emit();
            }
        };
        this.productId = 1;
        this.maxQuantity = 150;
        this.quantity = 0;
        this.disabled = false;
        this.context = 'default';
        this.ariaLabelAdd = 'Produkte zum Warenkorb hinzufügen';
        this.ariaLabelRemove = 'Produkte aus dem Warenkorb entfernen';
        this.ariaValueSingular = 'Ein Produkt im Warenkorb';
        this.ariaValuePlural = 'Produkte im Warenkorb';
        this.ariaLabeledBy = null;
        this.debounceTime = 500;
        this.mouseOverSub = false;
        this.hitLimit = false;
    }
    /**
     * Checks on quality change if user selected max amount.
     */
    onQuantityChange() {
        this.hitLimit = this.quantity >= this.maxQuantity;
    }
    /**
     * Component will render.
     */
    componentWillRender() {
        this.isDisabled = this.disabled || this.maxQuantity === 0;
        this.hitLimit = this.quantity >= this.maxQuantity;
        if (this.innerQuantity && this.innerQuantity !== this.quantity) {
            this.quantity = this.innerQuantity;
        }
    }
    /**
     * Renders the component.
     *
     * @returns {JSX.Element} The rendered component.
     */
    render() {
        return (h(Host, { key: '04587bdeab3c7cf08d1ccf9695b2757885fe2d28' }, h("div", { key: '17c6ef348af3cd4d41754e0b7b03ea854aaa5ef7', class: {
                'vx-add-to-cart-count': true,
                'vx-add-to-cart-count--open': this.quantity > 0,
                'vx-add-to-cart-count--disabled': this.isDisabled,
            }, role: this.quantity ? 'spinbutton' : 'button', "aria-label": this.ariaLabelAdd, "aria-labelledby": this.ariaLabeledBy }, h("button", { key: 'c9fc4c1952693024560dd191fa349d02c26968c9', class: {
                'vx-add-to-cart-count__sub': true,
            }, "aria-label": this.ariaLabelRemove, tabindex: !this.isDisabled && this.hitLimit ? null : -1, onMouseOver: () => {
                this.mouseOverSub = true;
            }, onMouseOut: () => {
                this.mouseOverSub = false;
            }, onClick: this.removeItem, onKeyDown: this.handleKeyPress }, this.quantity > 1
            ? h("vx-icon", { icon: "subtract", invert: this.mouseOverSub })
            : h("vx-icon", { icon: "delete", invert: (this.mouseOverSub) })), h("div", { key: 'b5c31f7366041feb3c3748a05259fc31402c289c', class: 'vx-add-to-cart-count__number' }, h("input", { key: 'f84a3e044d1903b06b3b88fbc64a3a836b1fdc7c', type: 'number', min: 0, max: this.maxQuantity, step: 1, value: this.quantity, tabindex: "-1", disabled: this.isDisabled || !this.quantity, onChange: this.handleInputChange, onKeyDown: this.handleKeyPress, "aria-label": 'Anzahl der Produkte', ref: (el) => {
                this.numberInput = el;
            } })), h("button", { key: '10fe7ace0df4d706db6571e6fc2bc6abd88531a7', class: {
                'vx-add-to-cart-count__add': true,
                'vx-add-to-cart-count__add--disabled': this.isDisabled || this.hitLimit,
            }, "aria-label": this.ariaLabelAdd, onClick: this.addItem, onFocus: this.handleFocus.bind(this), onKeyDown: this.handleKeyPress, tabindex: this.isDisabled || this.hitLimit ? -1 : null }, this.quantity > 0
            ? h("vx-icon", { icon: "add", invert: true, filter: this.isDisabled || this.hitLimit ? 'invert(50%)' : null })
            : h("vx-icon", { icon: "add_shopping_cart", invert: true, filter: (this.isDisabled || this.hitLimit) ? 'invert(50%)' : null })))));
    }
    static get is() { return "vx-add-to-cart-count"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["vx-add-to-cart-count.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["vx-add-to-cart-count.css"]
        };
    }
    static get properties() {
        return {
            "productId": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Product ID."
                },
                "attribute": "product-id",
                "reflect": false,
                "defaultValue": "1"
            },
            "maxQuantity": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Max quantity."
                },
                "attribute": "max-quantity",
                "reflect": false,
                "defaultValue": "150"
            },
            "quantity": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Article quantity."
                },
                "attribute": "quantity",
                "reflect": false,
                "defaultValue": "0"
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Disable button."
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "context": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Context used for tracking."
                },
                "attribute": "context",
                "reflect": false,
                "defaultValue": "'default'"
            },
            "ariaLabelAdd": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Aria label for adding products."
                },
                "attribute": "aria-label-add",
                "reflect": false,
                "defaultValue": "'Produkte zum Warenkorb hinzuf\u00FCgen'"
            },
            "ariaLabelRemove": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Aria label for adding products."
                },
                "attribute": "aria-label-remove",
                "reflect": false,
                "defaultValue": "'Produkte aus dem Warenkorb entfernen'"
            },
            "ariaValueSingular": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Aria value text singular."
                },
                "attribute": "aria-value-singular",
                "reflect": false,
                "defaultValue": "'Ein Produkt im Warenkorb'"
            },
            "ariaValuePlural": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Aria value text plural."
                },
                "attribute": "aria-value-plural",
                "reflect": false,
                "defaultValue": "'Produkte im Warenkorb'"
            },
            "ariaLabeledBy": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string | null",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Aria labeled by."
                },
                "attribute": "aria-labeled-by",
                "reflect": false,
                "defaultValue": "null"
            },
            "debounceTime": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Debounce time for search event."
                },
                "attribute": "debounce-time",
                "reflect": false,
                "defaultValue": "500"
            }
        };
    }
    static get states() {
        return {
            "mouseOverSub": {},
            "hitLimit": {}
        };
    }
    static get events() {
        return [{
                "method": "handleQuantityChange",
                "name": "handleQuantityChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Handle quantity change event (SAME AS on vx-add-to-cart)."
                },
                "complexType": {
                    "original": "{\n        quantity: number,\n        productId: number,\n        context: string,\n        increased: boolean\n    }",
                    "resolved": "{ quantity: number; productId: number; context: string; increased: boolean; }",
                    "references": {}
                }
            }, {
                "method": "addToCartFocus",
                "name": "addToCartFocus",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Handle focus event."
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }];
    }
    static get watchers() {
        return [{
                "propName": "quantity",
                "methodName": "onQuantityChange"
            }];
    }
}
//# sourceMappingURL=vx-add-to-cart-count.js.map
